import { supabase } from '../providers/supabase/supabase';

export type DispatchStatus =
    | 'planned'
    | 'loading'
    | 'loading_done'
    | 'dc_created'
    | 'dispatched'
    | 'delivered'
    | 'cancelled';

export type PaymentStatus = 'due' | 'partial' | 'paid';

export interface Dispatch {
    id: string;
    dispatch_no: string | null;
    dispatch_date: string;
    company_id: number | null;
    transporter_id: number | null;
    transporter_name: string | null;
    vehicle_no: string | null;
    vehicle_length_ft: number | null;
    driver_name: string | null;
    driver_mobile: string | null;
    source_location: string | null;
    destination: string | null;
    tonnage: number | null;
    freight_cost: number | null;
    status: DispatchStatus;
    payment_status: PaymentStatus;
    paid_amount: number | null;
    delivery_challan_id: number | null;
    enquiry_id: number | null;
    short_code: string | null;
    notes: string | null;
    items: any[] | null;
    created_at: string;
}

export interface Transporter {
    id: number;
    name: string;
    phone: string | null;
    is_active: boolean;
}

export interface Enquiry {
    id: number;
    enquiry_date: string;
    vehicle_length_ft: number | null;
    loading_location: string | null;
    unloading_location: string | null;
    tonnage: number | null;
    description: string | null;
    status: 'open' | 'booked' | 'cancelled';
    booked_transporter_id: number | null;
}

export interface EnquiryRecipient {
    id: number;
    enquiry_id: number;
    transporter_id: number;
    quote_amount: number | null;
    quoted_at: string | null;
}

export interface DispatchDocument {
    id: number;
    dispatch_id: string;
    doc_type: string | null;
    file_name: string;
    file_path: string | null;
    created_at: string;
}

export const STATUS_LABEL: Record<DispatchStatus, string> = {
    planned: 'PLANNED',
    loading: 'LOADING',
    loading_done: 'LOADED',
    dc_created: 'DC CREATED',
    dispatched: 'IN TRANSIT',
    delivered: 'DELIVERED',
    cancelled: 'CANCELLED',
};

export const ACTIVE_STATUSES: DispatchStatus[] = [
    'planned',
    'loading',
    'loading_done',
    'dc_created',
    'dispatched',
];

export const shortId = (d: Pick<Dispatch, 'id' | 'short_code'>) =>
    (d.short_code || d.id.replace(/-/g, '').slice(0, 6)).toUpperCase();

export const formatINR = (n: number | null | undefined) =>
    new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0,
    }).format(n ?? 0);

export const dueAmount = (d: Dispatch) => {
    if (d.status === 'cancelled') return 0;
    if (d.payment_status === 'paid') return 0;
    return Math.max((d.freight_cost ?? 0) - (d.paid_amount ?? 0), 0);
};

export const whatsappLink = (phone: string | null, text: string) => {
    if (!phone) return null;
    let digits = phone.replace(/\D/g, '');
    if (digits.length === 10) digits = `91${digits}`;
    if (digits.length < 11) return null;
    return `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;
};

const fail = (error: { message: string } | null) => {
    if (error) throw new Error(error.message);
};

export async function fetchDispatches(): Promise<Dispatch[]> {
    const { data, error } = await supabase
        .from('dispatch_orders')
        .select('*')
        .order('dispatch_date', { ascending: false })
        .order('created_at', { ascending: false })
        .range(0, 4999);
    fail(error);
    return (data ?? []) as Dispatch[];
}

export async function fetchDocCounts(): Promise<Record<string, number>> {
    const { data, error } = await supabase
        .from('dispatch_documents')
        .select('dispatch_id')
        .range(0, 9999);
    fail(error);
    const out: Record<string, number> = {};
    (data ?? []).forEach((r: any) => {
        out[r.dispatch_id] = (out[r.dispatch_id] ?? 0) + 1;
    });
    return out;
}

export async function fetchTransporters(): Promise<Transporter[]> {
    const { data, error } = await supabase
        .from('transporters')
        .select('*')
        .order('name');
    fail(error);
    return (data ?? []) as Transporter[];
}

export async function addTransporter(name: string, phone: string) {
    const { error } = await supabase
        .from('transporters')
        .insert({ name: name.trim(), phone: phone.trim() || null });
    fail(error);
}

export async function removeTransporter(id: number) {
    const { error } = await supabase.from('transporters').delete().eq('id', id);
    fail(error);
}

export async function fetchEnquiries(): Promise<{
    enquiries: Enquiry[];
    recipients: EnquiryRecipient[];
}> {
    const [e, r] = await Promise.all([
        supabase
            .from('dispatch_enquiries')
            .select('*')
            .order('enquiry_date', { ascending: false })
            .order('id', { ascending: false }),
        supabase.from('dispatch_enquiry_recipients').select('*'),
    ]);
    fail(e.error);
    fail(r.error);
    return {
        enquiries: (e.data ?? []) as Enquiry[],
        recipients: (r.data ?? []) as EnquiryRecipient[],
    };
}

export async function createEnquiry(
    values: Omit<Enquiry, 'id' | 'status' | 'booked_transporter_id'>,
    transporterIds: number[],
) {
    const { data, error } = await supabase
        .from('dispatch_enquiries')
        .insert(values)
        .select('id')
        .single();
    fail(error);
    if (transporterIds.length) {
        const { error: e2 } = await supabase
            .from('dispatch_enquiry_recipients')
            .insert(
                transporterIds.map(t => ({
                    enquiry_id: data!.id,
                    transporter_id: t,
                })),
            );
        fail(e2);
    }
    return data!.id as number;
}

export async function saveQuote(recipientId: number, amount: number | null) {
    const { error } = await supabase
        .from('dispatch_enquiry_recipients')
        .update({
            quote_amount: amount,
            quoted_at: amount != null ? new Date().toISOString() : null,
        })
        .eq('id', recipientId);
    fail(error);
}

export async function setEnquiryStatus(
    id: number,
    status: Enquiry['status'],
    transporterId: number | null = null,
) {
    const { error } = await supabase
        .from('dispatch_enquiries')
        .update({ status, booked_transporter_id: transporterId })
        .eq('id', id);
    fail(error);
}

export async function deleteEnquiry(id: number) {
    const { error } = await supabase
        .from('dispatch_enquiries')
        .delete()
        .eq('id', id);
    fail(error);
}

export function newDispatchNo() {
    const d = new Date();
    const p = (n: number) => String(n).padStart(2, '0');
    return `DSP/${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${Math.random()
        .toString(36)
        .slice(2, 6)
        .toUpperCase()}`;
}

export async function saveDispatch(
    values: Partial<Dispatch> & { id?: string },
): Promise<void> {
    const payload: any = { ...values };
    if (!payload.id) {
        delete payload.id;
        payload.dispatch_no = payload.dispatch_no || newDispatchNo();
        const { error } = await supabase.from('dispatch_orders').insert(payload);
        fail(error);
    } else {
        const id = payload.id;
        delete payload.id;
        delete payload.created_at;
        payload.updated_at = new Date().toISOString();
        const { error } = await supabase
            .from('dispatch_orders')
            .update(payload)
            .eq('id', id);
        fail(error);
    }
}

export async function deleteDispatch(id: string) {
    const { error } = await supabase.from('dispatch_orders').delete().eq('id', id);
    fail(error);
}

export async function fetchDocuments(dispatchId: string) {
    const { data, error } = await supabase
        .from('dispatch_documents')
        .select('*')
        .eq('dispatch_id', dispatchId)
        .order('created_at', { ascending: false });
    fail(error);
    return (data ?? []) as DispatchDocument[];
}

const BUCKET = 'dispatch-docs';

export async function uploadDocument(dispatchId: string, file: File, docType: string) {
    const path = `${dispatchId}/${Date.now()}-${file.name.replace(/[^\w.-]+/g, '_')}`;
    const up = await supabase.storage.from(BUCKET).upload(path, file);
    fail(up.error as any);
    const { error } = await supabase.from('dispatch_documents').insert({
        dispatch_id: dispatchId,
        doc_type: docType,
        file_name: file.name,
        file_path: path,
    });
    fail(error);
}

export async function documentUrl(path: string) {
    const { data, error } = await supabase.storage
        .from(BUCKET)
        .createSignedUrl(path, 300);
    fail(error as any);
    return data!.signedUrl;
}

export async function deleteDocument(doc: DispatchDocument) {
    if (doc.file_path) await supabase.storage.from(BUCKET).remove([doc.file_path]);
    const { error } = await supabase
        .from('dispatch_documents')
        .delete()
        .eq('id', doc.id);
    fail(error);
}

export async function fetchCompanies(): Promise<{ id: number; name: string }[]> {
    const { data, error } = await supabase
        .from('companies')
        .select('id,name')
        .order('name')
        .range(0, 4999);
    fail(error);
    return (data ?? []) as any;
}

export async function fetchChallanRefs(): Promise<{ id: number; reference: string }[]> {
    const { data, error } = await supabase
        .from('delivery_challans')
        .select('id,reference')
        .order('reference', { ascending: false })
        .range(0, 4999);
    fail(error);
    return (data ?? []) as any;
}

export async function backupAll() {
    const tables = [
        'transporters',
        'dispatch_enquiries',
        'dispatch_enquiry_recipients',
        'dispatch_orders',
        'dispatch_documents',
    ];
    const out: Record<string, any[]> = {};
    for (const t of tables) {
        const { data, error } = await supabase.from(t).select('*').range(0, 9999);
        fail(error);
        out[t] = data ?? [];
    }
    return { version: 1, exported_at: new Date().toISOString(), ...out };
}

export async function restoreAll(json: any) {
    const order: [string, string][] = [
        ['transporters', 'id'],
        ['dispatch_enquiries', 'id'],
        ['dispatch_enquiry_recipients', 'id'],
        ['dispatch_orders', 'id'],
        ['dispatch_documents', 'id'],
    ];
    for (const [t, key] of order) {
        const rows = json?.[t];
        if (!Array.isArray(rows) || !rows.length) continue;
        for (let i = 0; i < rows.length; i += 200) {
            const { error } = await supabase
                .from(t)
                .upsert(rows.slice(i, i + 200), { onConflict: key });
            fail(error);
        }
    }
}

export function downloadFile(name: string, content: string, mime: string) {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
}
